/* structure.js — Arbre du conteneur (MP4/MOV, MKV/EBML, WAV/AVI-RIFF) et ses
 * anomalies. Lecture par plages, bornes contrôlées, limites de profondeur.
 *
 * MediaStructure.parse(read, fileSize) -> Promise<{format,valid,truncated,nodeCount,issues,anomalies,root}>
 * MediaStructure.survey(read, fileSize) -> Promise<{moov,mdat,moovFirst,truncatedAt,invalidAt}|null>
 * MediaStructure.measure(survey) -> valeurs mesurées pour le modèle
 *   read(offset, length) -> Promise<Uint8Array>
 *   node = { name, offset, size, headerSize, depth, container, decoded, anomaly, children } */
'use strict';

var MAX_NODES = 5000;
var MAX_DEPTH = 20;

function u32be(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }
function u32le(b, o) { return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0; }
function u64be(b, o) {
  var hi = BigInt(u32be(b, o)), lo = BigInt(u32be(b, o + 4));
  return Number((hi << 32n) | lo);
}
function fourcc(b, o) {
  var s = '';
  for (var i = 0; i < 4; i++) { var c = b[o + i]; s += (c >= 32 && c < 127) ? String.fromCharCode(c) : '·'; }
  return s;
}

// Anomalie structurée : { kind: 'truncated' | 'invalid', name, offset }.
function flag(ctx, kind, name, offset) {
  if (kind === 'truncated') ctx.truncated = true;
  ctx.anomalies.push({ kind: kind, name: name, offset: offset });
}

/* ---------- ISO BMFF (MP4/MOV) ---------- */
var MP4_CONTAINERS = {
  moov: 1, trak: 1, mdia: 1, minf: 1, stbl: 1, dinf: 1, edts: 1, udta: 1,
  mvex: 1, moof: 1, traf: 1, mfra: 1, meta: 1, ipro: 1, sinf: 1, schi: 1, wave: 1
};

async function parseMP4(read, start, end, depth, ctx) {
  var nodes = [], off = start;
  while (off + 8 <= end && ctx.count < MAX_NODES) {
    var hdr = await read(off, 16);
    if (hdr.length < 8) { flag(ctx, 'truncated', null, off); break; }
    var size = u32be(hdr, 0), type = fourcc(hdr, 4), headerSize = 8;
    if (size === 1) {
      if (hdr.length < 16) { flag(ctx, 'truncated', type, off); break; }
      size = u64be(hdr, 8); headerSize = 16;
    } else if (size === 0) { size = end - off; }

    var anomaly = null;
    if (size < headerSize) { anomaly = 'taille déclarée invalide (' + size + ')'; ctx.issues.push(type + ' @' + off + ' : ' + anomaly); flag(ctx, 'invalid', type, off); size = end - off; }
    if (off + size > end) { anomaly = 'dépasse le parent/fichier — troncature probable'; ctx.issues.push(type + ' @' + off + ' : ' + anomaly); flag(ctx, 'truncated', type, off); }

    var node = { name: type, offset: off, size: size, headerSize: headerSize, depth: depth, container: false, decoded: null, anomaly: anomaly, children: [] };
    ctx.count++;
    var contentStart = off + headerSize, contentEnd = Math.min(off + size, end);

    if (MP4_CONTAINERS[type] && depth < MAX_DEPTH && !anomaly) {
      node.container = true;
      node.children = await parseMP4(read, contentStart, contentEnd, depth + 1, ctx);
    } else if (type === 'ftyp' && contentEnd - contentStart >= 4) {
      var c = await read(contentStart, 4);
      if (c.length >= 4) node.decoded = 'marque majeure : ' + fourcc(c, 0);
    }
    nodes.push(node);
    if (size <= 0) break;
    off += size;
  }
  return nodes;
}

/* ---------- EBML (MKV/WebM) ---------- */
var EBML_MASTERS = {
  '1a45dfa3': 'EBML', '18538067': 'Segment', '114d9b74': 'SeekHead', '4dbb': 'Seek',
  '1549a966': 'Info', '1654ae6b': 'Tracks', 'ae': 'TrackEntry', 'e0': 'Video', 'e1': 'Audio',
  '1f43b675': 'Cluster', 'a0': 'BlockGroup', '1c53bb6b': 'Cues', 'bb': 'CuePoint',
  '1254c367': 'Tags', '7373': 'Tag', '67c8': 'SimpleTag', '1941a469': 'Attachments',
  '61a7': 'AttachedFile', '1043a770': 'Chapters', '45b9': 'EditionEntry', 'b6': 'ChapterAtom'
};
var EBML_NAMES = {
  '4282': 'DocType', '2ad7b1': 'TimestampScale', '4489': 'Duration', '4d80': 'MuxingApp',
  '5741': 'WritingApp', '83': 'TrackType', '86': 'CodecID', '22b59c': 'Language', 'd7': 'TrackNumber'
};

function readVint(b, pos, keepMarker) {
  if (pos >= b.length) return null;
  var first = b[pos];
  if (first === 0) return null;
  var mask = 0x80, len = 1;
  while (len <= 8 && !(first & mask)) { mask >>= 1; len++; }
  if (len > 8 || pos + len > b.length) return null;
  var val = keepMarker ? first : (first & (mask - 1));
  var allOnes = (first & (mask - 1)) === (mask - 1);
  for (var i = 1; i < len; i++) { val = val * 256 + b[pos + i]; if (b[pos + i] !== 0xff) allOnes = false; }
  return { len: len, val: val, allOnes: allOnes };
}
function idHex(b, pos, len) { var s = ''; for (var i = 0; i < len; i++) s += ('0' + b[pos + i].toString(16)).slice(-2); return s; }

async function parseEBML(read, start, end, depth, ctx) {
  var nodes = [], off = start;
  while (off < end && ctx.count < MAX_NODES) {
    var hdr = await read(off, 12);
    if (hdr.length < 2) { if (off < end) flag(ctx, 'truncated', null, off); break; }
    var id = readVint(hdr, 0, true);
    if (!id) { ctx.issues.push('identifiant EBML illisible @' + off); flag(ctx, 'invalid', null, off); ctx.truncated = true; break; }
    var sz = readVint(hdr, id.len, false);
    if (!sz) { flag(ctx, 'truncated', null, off); break; }
    var headerSize = id.len + sz.len;
    var hex = idHex(hdr, 0, id.len);
    var name = EBML_MASTERS[hex] || EBML_NAMES[hex] || ('0x' + hex);
    var size = sz.allOnes ? (end - off - headerSize) : sz.val; // taille inconnue → jusqu'à la fin du parent

    var anomaly = null;
    if (off + headerSize + size > end) { anomaly = 'dépasse le parent/fichier — troncature probable'; ctx.issues.push(name + ' @' + off + ' : ' + anomaly); flag(ctx, 'truncated', name, off); size = end - off - headerSize; }

    var node = { name: name, offset: off, size: size, headerSize: headerSize, depth: depth, container: false, decoded: null, anomaly: anomaly, children: [] };
    ctx.count++;
    var contentStart = off + headerSize, contentEnd = Math.min(contentStart + size, end);

    if (EBML_MASTERS[hex] && depth < MAX_DEPTH && !anomaly && size > 0) {
      node.container = true;
      node.children = await parseEBML(read, contentStart, contentEnd, depth + 1, ctx);
    } else if (EBML_NAMES[hex] && size > 0 && size <= 64) {
      var c = await read(contentStart, Math.min(size, 64));
      node.decoded = decodeEbmlValue(hex, c);
    }
    nodes.push(node);
    var next = contentStart + size;
    if (next <= off) break;
    off = next;
  }
  return nodes;
}
function decodeEbmlValue(hex, b) {
  if (hex === '4282' || hex === '4d80' || hex === '5741' || hex === '86' || hex === '22b59c') {
    var s = ''; for (var i = 0; i < b.length; i++) { var c = b[i]; if (c === 0) break; s += (c >= 32 && c < 127) ? String.fromCharCode(c) : '·'; }
    return s || null;
  }
  var n = 0; for (var j = 0; j < b.length; j++) n = n * 256 + b[j];
  return String(n);
}

/* ---------- RIFF (WAV/AVI) ---------- */
async function parseRIFF(read, start, end, depth, ctx) {
  var nodes = [], off = start;
  while (off + 8 <= end && ctx.count < MAX_NODES) {
    var hdr = await read(off, 12);
    if (hdr.length < 8) { flag(ctx, 'truncated', null, off); break; }
    var id = fourcc(hdr, 0), size = u32le(hdr, 4), headerSize = 8;
    var anomaly = null;
    if (off + 8 + size > end) { anomaly = 'dépasse le fichier — troncature probable'; ctx.issues.push(id + ' @' + off + ' : ' + anomaly); flag(ctx, 'truncated', id, off); size = end - off - 8; }
    var node = { name: id, offset: off, size: size, headerSize: headerSize, depth: depth, container: false, decoded: null, anomaly: anomaly, children: [] };
    ctx.count++;
    var contentStart = off + 8, contentEnd = Math.min(contentStart + size, end);
    if ((id === 'RIFF' || id === 'LIST') && depth < MAX_DEPTH && !anomaly) {
      node.container = true;
      var form = hdr.length >= 12 ? fourcc(hdr, 8) : '';
      node.decoded = 'forme : ' + form;
      node.children = await parseRIFF(read, contentStart + 4, contentEnd, depth + 1, ctx);
    } else if (id === 'fmt ' && size >= 16) {
      var c = await read(contentStart, 16);
      node.decoded = 'format audio ' + u16le(c, 0) + ', ' + u16le(c, 2) + ' canaux, ' + u32le(c, 4) + ' Hz';
    }
    nodes.push(node);
    var advance = 8 + size + (size % 2); // chunks RIFF alignés sur 2 octets
    if (advance <= 0) break;
    off += advance;
  }
  return nodes;
}
function u16le(b, o) { return b[o] | (b[o + 1] << 8); }

/* ---------- Détection & point d'entrée ---------- */
async function detect(read) {
  var b = await read(0, 16);
  if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return 'ebml';
  if (b.length >= 12 && fourcc(b, 0) === 'RIFF') return 'riff';
  if (b.length >= 8) { var t = fourcc(b, 4); if (t === 'ftyp' || t === 'moov' || t === 'free' || t === 'mdat' || t === 'skip' || t === 'wide') return 'mp4'; }
  return null;
}

async function parse(read, fileSize) {
  var ctx = { count: 0, truncated: false, issues: [], anomalies: [] };
  var fmt = await detect(read);
  var rootNodes = [];
  if (fmt === 'mp4') rootNodes = await parseMP4(read, 0, fileSize, 0, ctx);
  else if (fmt === 'ebml') rootNodes = await parseEBML(read, 0, fileSize, 0, ctx);
  else if (fmt === 'riff') rootNodes = await parseRIFF(read, 0, fileSize, 0, ctx);
  return {
    format: fmt,
    supported: !!fmt,
    valid: !!fmt && !ctx.truncated && ctx.issues.length === 0,
    truncated: ctx.truncated,
    nodeCount: ctx.count,
    capped: ctx.count >= MAX_NODES,
    issues: ctx.issues.slice(0, 50),
    anomalies: ctx.anomalies.slice(0, 50),
    root: rootNodes
  };
}

/* ---------- Survol MP4/MOV (premier niveau seulement) ----------
 * Une lecture par bloc de premier niveau : position du moov par rapport au
 * premier mdat, et dernier bloc qui dépasse la fin du fichier. Renvoie null
 * hors MP4/MOV, ou si le premier niveau compte trop de blocs pour conclure. */
async function survey(read, fileSize) {
  if (await detect(read) !== 'mp4') return null;
  var off = 0, n = 0, moov = null, mdat = null, truncatedAt = null, invalidAt = null;
  while (off + 8 <= fileSize) {
    if (++n > MAX_NODES) return null;
    var hdr = await read(off, 16);
    if (hdr.length < 8) { truncatedAt = { name: null, offset: off }; break; }
    var size = u32be(hdr, 0), type = fourcc(hdr, 4), headerSize = 8;
    if (size === 1) {
      if (hdr.length < 16) { truncatedAt = { name: type, offset: off }; break; }
      size = u64be(hdr, 8); headerSize = 16;
    } else if (size === 0) size = fileSize - off;
    if (size < headerSize) { invalidAt = { name: type, offset: off }; break; }
    if (type === 'moov' && !moov) moov = { offset: off };
    if (type === 'mdat' && !mdat) mdat = { offset: off };
    if (off + size > fileSize) { truncatedAt = { name: type, offset: off }; break; }
    off += size;
  }
  if (!truncatedAt && !invalidAt && off < fileSize) truncatedAt = { name: null, offset: off }; // reste < 8 octets
  return {
    format: 'mp4',
    moov: moov, mdat: mdat,
    moovFirst: moov && mdat ? moov.offset < mdat.offset : null,
    truncatedAt: truncatedAt,
    invalidAt: invalidAt
  };
}

// Valeurs mesurées tirées du survol ; une mesure impossible n'en produit pas.
function measure(sv) {
  if (!sv) return [];
  var FILE = { kind: 'container' };
  function v(property, value) {
    return { subject: FILE, property: property, value: value, origin: 'measured', scope: 'file', source: 'structure' };
  }
  var out = [];
  if (sv.moovFirst !== null) out.push(v('moovPosition', sv.moovFirst ? 'before-mdat' : 'after-mdat'));
  if (!sv.invalidAt) out.push(v('truncated', !!sv.truncatedAt));
  return out;
}

var api = { parse: parse, survey: survey, measure: measure, MAX_NODES: MAX_NODES, MAX_DEPTH: MAX_DEPTH };
globalThis.MediaStructure = api;
export default api;
